import { ICellRendererParams } from "@ag-grid-community/core";
import { GenericTableCellFormField } from "gen/model/genericTableCellFormField";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import React, { useRef } from "react";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormSimpleDateInput } from "shared/components/input/date/SimpleDateInput";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormSimpleSelect } from "shared/components/input/select/SimpleSelect";
import { FormSimpleMoneyInput } from "shared/components/input/text/SimpleMoneyInput";
import { FormSimpleNumberInput } from "shared/components/input/text/SimpleNumberInput";
import { FormSimpleTextInput } from "shared/components/input/text/SimpleTextInput";
import { MaybernTableFocusCellRendererWrapper } from "shared/components/table/maybern/MaybernTableFocusCellRendererWrapper";

export interface DataTableCellEditorProps<T> {
  value: T | undefined;
  formField: GenericTableCellFormField;
  isDisabled: boolean;
  shouldUnregister: boolean | undefined;
  params: ICellRendererParams;
}

export const DataTableCellEditorSelect: React.FC<
  DataTableCellEditorProps<string>
> = ({ value, formField, isDisabled, shouldUnregister, params }) => {
  const ref = useRef<never>(null);
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <FormSimpleSelect
        ref={ref}
        name={formField.name}
        title={formField.title!}
        isDisabled={isDisabled}
        shouldUnregister={shouldUnregister}
        defaultValue={value ?? ""}
        variant="simple-table"
      >
        {formField.options?.map(({ value, rich_value }) => (
          <SelectOption key={`select_${value}`} value={value}>
            {rich_value}
          </SelectOption>
        ))}
      </FormSimpleSelect>
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const DataTableCellEditorText: React.FC<
  DataTableCellEditorProps<string>
> = ({ value, formField, isDisabled, shouldUnregister, params }) => {
  const ref = useRef<never>(null);
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <FormSimpleTextInput
        ref={ref}
        name={formField.name}
        title={formField.title!}
        variant="simple-table"
        size="sm"
        defaultValue={value ?? ""}
        isDisabled={isDisabled}
        shouldUnregister={shouldUnregister}
      />
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const DataTableCellEditorDate: React.FC<
  DataTableCellEditorProps<string>
> = ({ value, formField, isDisabled, shouldUnregister, params }) => {
  const ref = useRef<never>(null);
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <FormSimpleDateInput
        ref={ref}
        name={formField.name}
        title={formField.title!}
        variant="simple-table"
        size="sm"
        defaultValue={value ?? ""}
        isDisabled={isDisabled}
        shouldUnregister={shouldUnregister}
      />
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const DataTableCellEditorBoolean: React.FC<
  DataTableCellEditorProps<boolean>
> = ({ value, formField, isDisabled, shouldUnregister, params }) => {
  const ref = useRef<never>(null);
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <FormCheckbox
        ref={ref}
        name={formField.name}
        defaultValue={value}
        isDisabled={isDisabled}
        shouldUnregister={shouldUnregister}
      />
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const DataTableCellEditorMoney: React.FC<
  DataTableCellEditorProps<MoneyTableCellValue>
> = ({ value, formField, isDisabled, shouldUnregister, params }) => {
  const ref = useRef<never>(null);
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <FormSimpleMoneyInput
        ref={ref}
        name={formField.name}
        placeholder={formField.title!}
        variant="simple-table"
        size="sm"
        currency={value?.currency ?? undefined}
        showCurrencyPrefix={false}
        showCurrencyIcon={false}
        defaultValue={value?.amount}
        isDisabled={isDisabled}
        shouldUnregister={shouldUnregister}
      />
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const DataTableCellEditorPercent: React.FC<
  DataTableCellEditorProps<string>
> = ({ value, formField, isDisabled, shouldUnregister, params }) => {
  const ref = useRef<never>(null);
  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <FormSimpleNumberInput
        ref={ref}
        name={formField.name}
        placeholder={formField.title!}
        variant="simple-table"
        size="sm"
        valueType="percent"
        defaultValue={value ?? ""}
        isDisabled={isDisabled}
        shouldUnregister={shouldUnregister}
      />
    </MaybernTableFocusCellRendererWrapper>
  );
};
